import React, { useState } from "react";
import { motion } from "motion/react";
import { Loader2, Trash2, Check, Sparkles, Pencil } from "lucide-react";

export default function TaskDraftCard({ draft, onApprove, onDelete, onEdit, isApproved }) {
  const [isApproving, setIsApproving] = useState(false);

  const title = draft.payloadJson?.title || draft.title || "Без названия";
  const description = draft.payloadJson?.description || draft.description || "";
  const expectedResult = draft.payloadJson?.expectedResult || "";

  const handleApprove = async (e) => {
    e.stopPropagation();
    setIsApproving(true);
    await onApprove(draft.id);
    setIsApproving(false);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(draft.id);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    if (onEdit && !isApproved) {
      onEdit(draft);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      onClick={() => onEdit && !isApproved && onEdit(draft)}
      className={`border rounded-xl p-5 relative transition-all ${
        isApproved 
          ? "bg-neutral-900/40 border-green-500/30 opacity-70 cursor-default" 
          : "bg-[#1c1c1c] border-accent-900/40 hover:border-accent-500/50 hover:shadow-lg hover:shadow-accent-900/10 group cursor-pointer"
      }`}
    >
      <div className="mb-3 flex items-start justify-between gap-2">
        <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-md border font-semibold ${
          isApproved 
            ? "bg-green-500/10 text-green-400 border-green-500/20" 
            : "bg-accent-500/10 text-accent-400 border-accent-500/20"
        }`}>
          {isApproved ? <Check className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
          {isApproved ? "В работе" : "Сгенерировано ИИ"}
        </span>

        {!isApproved && (
          <button
            type="button"
            onClick={handleEdit}
            className="opacity-80 group-hover:opacity-100 flex items-center gap-1 text-xs text-neutral-400 hover:text-white bg-neutral-800/80 hover:bg-neutral-800 border border-neutral-700/50 px-2 py-1 rounded-md transition-all shadow-sm"
            title="Редактировать черновик"
          >
            <Pencil className="w-3 h-3 text-accent-400" />
            <span>Изменить</span>
          </button>
        )}
      </div>

      <div className="mb-3">
        <h3 className={`text-lg font-normal line-clamp-2 leading-tight transition-colors ${
          isApproved ? "text-neutral-400" : "text-neutral-100 group-hover:text-accent-300"
        }`}>
          {title}
        </h3>
      </div>

      <div className={`space-y-3 ${isApproved ? "mb-0" : "mb-5"}`}>
        {description && (
          <div>
            <span className="block text-[10px] uppercase tracking-wider font-semibold text-neutral-500 mb-0.5">Описание</span>
            <p className={`text-sm font-light line-clamp-3 ${isApproved ? "text-neutral-500" : "text-neutral-300"}`}>
              {description}
            </p>
          </div>
        )}

        {expectedResult && (
          <div>
            <span className="block text-[10px] uppercase tracking-wider font-semibold text-neutral-500 mb-0.5">Ожидаемый результат (DoD)</span>
            <p className={`text-sm font-light line-clamp-2 ${isApproved ? "text-neutral-500" : "text-neutral-300"}`}>
              {expectedResult}
            </p>
          </div>
        )}
      </div>

      {!isApproved && (
        <div className="flex items-center gap-2 pt-3 border-t border-neutral-800/60">
          <button
            type="button"
            onClick={handleDelete}
            className="p-2 text-neutral-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
            title="Отклонить черновик"
          >
            <Trash2 className="w-4 h-4" />
          </button>

          <button
            type="button"
            onClick={handleEdit}
            className="flex-1 flex justify-center items-center gap-1.5 bg-neutral-800/70 hover:bg-neutral-800 text-neutral-300 hover:text-white border border-neutral-700/40 font-medium text-xs py-2 rounded-lg transition-colors"
          >
            <Pencil className="w-3.5 h-3.5 text-accent-400" />
            <span>Изменить</span>
          </button>

          <button
            type="button"
            onClick={handleApprove}
            disabled={isApproving}
            className="flex-1 flex justify-center items-center gap-1.5 bg-accent-600/20 hover:bg-accent-600/40 text-accent-400 font-medium text-xs py-2 rounded-lg transition-colors disabled:opacity-50 border border-accent-500/20"
          >
            {isApproving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            <span>Утвердить</span>
          </button>
        </div>
      )}
    </motion.div>
  );
}
